import { getDataSource } from '@/lib/data-source';

type StripeResponse = {
  stripe_config?: unknown;
};

export interface StripeCapabilities {
  card_payments: 'active' | 'inactive' | 'pending';
  transfers: 'active' | 'inactive' | 'pending';
  [key: string]: string;
}

export interface StripeRequirements {
  currently_due: string[];
  eventually_due: string[];
  [key: string]: string[] | string;
}

export interface StripeConfiguration {
  configured: boolean;
  live_mode: boolean;
  account_country?: string;
  account_currency?: string;
  configured_at?: string;
  capabilities?: StripeCapabilities;
  requirements?: StripeRequirements;
  business_type?: string;
  publishable_key_preview?: string;
}

export interface StripeConfigurationRequest {
  stripe_publishable_key: string;
  stripe_secret_key: string;
  stripe_webhook_secret?: string;
  stripe_account_country?: string;
  stripe_account_currency?: string;
  stripe_live_mode: boolean;
  stripe_business_type?: string;
}

export interface StripeConfigurationUpdateRequest {
  stripe_publishable_key?: string;
  stripe_secret_key?: string;
  stripe_webhook_secret?: string;
  stripe_account_country?: string;
  stripe_account_currency?: string;
  stripe_live_mode?: boolean;
  stripe_business_type?: string;
}

export interface StripeValidationRequest {
  stripe_publishable_key: string;
  stripe_secret_key: string;
}

export interface StripeConfigurationResponse {
  data: {
    stripe_config: StripeConfiguration;
  } | StripeConfiguration;
  meta?: {
    message: string;
  };
  jsonapi?: {
    version: string;
  };
}

export interface StripeAccountResponse {
  data: {
    account: {
      id: string;
      country: string;
      default_currency: string;
      business_type: string;
      capabilities: StripeCapabilities;
      requirements: StripeRequirements;
    };
  };
}

class StripeConfigurationService {
  private dataSource = getDataSource();

  /**
   * Get current Stripe configuration
   */
  async getStripeConfiguration(): Promise<StripeConfiguration> {
    try {
      const response = await this.dataSource.get<StripeConfigurationResponse>(
        '/api/v1/billing/stripe/config'
      );
      
      // Handle the response structure - API might return data directly or nested
      const responseData = response.data as unknown as StripeResponse;
      const configData = responseData?.stripe_config || response.data;
      
      return configData as StripeConfiguration;
    } catch (error) {
      console.error('Error fetching Stripe configuration:', error);
      throw error;
    }
  }

  /**
   * Create initial Stripe configuration
   */
  async createStripeConfiguration(data: StripeConfigurationRequest): Promise<StripeConfiguration> {
    try {
      const response = await this.dataSource.post<StripeConfigurationResponse>(
        '/api/v1/billing/stripe/config',
        data
      );
      
      const responseData = response.data as unknown as StripeResponse;
      const configData = responseData?.stripe_config || response.data;
      return configData as StripeConfiguration;
    } catch (error) {
      console.error('Error creating Stripe configuration:', error);
      throw error;
    }
  }

  /**
   * Update existing Stripe configuration
   */
  async updateStripeConfiguration(data: StripeConfigurationUpdateRequest): Promise<StripeConfiguration> {
    try {
      const response = await this.dataSource.put<StripeConfigurationResponse>(
        '/api/v1/billing/stripe/config',
        data
      );
      
      const responseData = response.data as unknown as StripeResponse;
      const configData = responseData?.stripe_config || response.data;
      return configData as StripeConfiguration;
    } catch (error) {
      console.error('Error updating Stripe configuration:', error);
      throw error;
    }
  }

  /**
   * Validate Stripe credentials
   */
  async validateStripeCredentials(data: StripeValidationRequest): Promise<{ valid: boolean; message?: string }> {
    try {
      const response = await this.dataSource.post<{ data?: { valid?: boolean; message?: string }; valid?: boolean; message?: string }>(
        '/api/v1/billing/stripe/config/validate',
        data
      );
      
      console.log('🔍 Validate credentials response:', response);
      console.log('🔍 Response.data:', response.data);
      
      // Handle the response structure
      const validationResult = response.data as { valid?: boolean; message?: string };
      
      return {
        valid: validationResult?.valid || false,
        message: validationResult?.message
      };
    } catch (error) {
      console.error('Error validating Stripe credentials:', error);
      throw error;
    }
  }

  /**
   * Test Stripe connection
   */
  async testStripeConnection(): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await this.dataSource.post<{ data?: { success?: boolean; message?: string }; success?: boolean; message?: string }>(
        '/api/v1/billing/stripe/config/test'
      );
      
      console.log('🔍 Test connection response:', response);
      console.log('🔍 Response.data:', response.data);
      
      // Handle the response structure
      const testResult = response.data as { success?: boolean; message?: string };
      
      // If the API returns a message, it's successful
      if (testResult?.message) {
        return {
          success: true,
          message: testResult.message
        };
      }
      
      // Fallback - check for success field
      return {
        success: testResult?.success || false,
        message: testResult?.message
      };
    } catch (error) {
      console.error('Error testing Stripe connection:', error);
      throw error;
    }
  }

  /**
   * Refresh Stripe capabilities
   */
  async refreshStripeCapabilities(): Promise<StripeConfiguration> {
    try {
      const response = await this.dataSource.post<StripeConfigurationResponse>(
        '/api/v1/billing/stripe/config/refresh'
      );
      
      const responseData = response.data as unknown as StripeResponse;
      const configData = responseData?.stripe_config || response.data;
      return configData as StripeConfiguration;
    } catch (error) {
      console.error('Error refreshing Stripe capabilities:', error);
      throw error;
    }
  }

  /**
   * Remove Stripe configuration
   */
  async removeStripeConfiguration(): Promise<{ success: boolean }> {
    try {
      const response = await this.dataSource.delete<{ data: { success: boolean } }>(
        '/api/v1/billing/stripe/config'
      );
      
      return response.data.data;
    } catch (error) {
      console.error('Error removing Stripe configuration:', error);
      throw error;
    }
  }

  /**
   * Get Stripe account details
   */
  async getStripeAccountDetails(): Promise<StripeAccountResponse['data']['account']> {
    try {
      const response = await this.dataSource.get<StripeAccountResponse>(
        '/api/v1/billing/stripe/config/account'
      );
      
      return response.data.data.account;
    } catch (error) {
      console.error('Error fetching Stripe account details:', error);
      throw error;
    }
  }
}

export const stripeConfigurationService = new StripeConfigurationService();
